/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Button } from '@mantine/core';
import { RiArrowRightLine } from '@remixicon/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
  isDatasetDeleteable,
  isDatasetFinetuneable,
} from '@fai/friendli-suite/features/products/dedicated-endpoints/datasets/dataset-status';
import { useDatasetStatus } from '@fai/friendli-suite/features/products/dedicated-endpoints/datasets/use-dataset-status';
import { initialValues } from '@fai/friendli-suite/features/products/dedicated-endpoints/finetuning/form-context';
import Paths from '@fai/friendli-suite/features/routes/paths';
import { DedicatedModelType } from '@fai/friendli-suite/features/types/dedicated-endpoints/models';
import { HideFromRelease } from '@fai/friendli-suite/shared/environments/hide-from-release';
import TransparentBackLinkButton from '@fai/friendli-suite/views/common/button/back-button/transparent-link';
import DatasetTag from '@fai/friendli-suite/views/dedicated-endpoints/projects/datasets/dataset-tag';
import DEProjectSubHeader from '@fai/friendli-suite/views/layout/product/dedicated-endpoints/projects/sub-layout/header';

import { useDatasetDetailHeader } from './use-dataset-detail-header';

export default function DatasetDetailHeader() {
  const { t } = useTranslation();
  const { teamId = '', projectId = '' } = useParams();
  const navigate = useNavigate();

  const { status } = useDatasetStatus();
  const { data } = useDatasetDetailHeader();

  const name = data.name ?? '(name)';
  const listPath = Paths.team(teamId).dedicatedEndpoints.project.detail(projectId).dataset.list;

  const navigateToCreateFinetuningWithDataset = () => {
    // navigate to create finetuning page with pre-selected dataset.
    // Ref: apps/friendli-suite/src/pages/dedicated-endpoints/projects/finetuning/create/use-get-initial-data.ts
    navigate(Paths.team(teamId).dedicatedEndpoints.project.detail(projectId).finetuning.create, {
      state: {
        dataset: {
          ...initialValues.model,
          type: DedicatedModelType.Native,
          native: { modelId: data.id, modelName: data.name },
        },
      },
    });
  };

  const finetuneButton = (
    <Button
      theme="cloud"
      size="sm"
      rightSection={<RiArrowRightLine size={16} />}
      disabled={!isDatasetFinetuneable(status)}
      onClick={navigateToCreateFinetuningWithDataset}
    >
      {t('dedicated_endpoints.project.fine_tune')}
    </Button>
  );

  const EllipsisMenu = (
    <DEProjectSubHeader.EllipsisMenu>
      <DEProjectSubHeader.EllipsisMenuItem
        disabled={!isDatasetDeleteable(status)}
        onClick={() => alert('TODO: open delete modal')}
      >
        {t('shared.delete')}
      </DEProjectSubHeader.EllipsisMenuItem>
    </DEProjectSubHeader.EllipsisMenu>
  );

  return (
    <DEProjectSubHeader
      titleSlot={
        <DEProjectSubHeader.TitleRow
          title={name}
          leftSlot={<DatasetTag />}
          rightSlot={
            <>
              {finetuneButton}
              <HideFromRelease isReleaseTarget={false}>{EllipsisMenu}</HideFromRelease>
            </>
          }
        />
      }
      backButtonSlot={
        <TransparentBackLinkButton to={listPath}>
          {t('dedicated_endpoints.project.back_to_datasets')}
        </TransparentBackLinkButton>
      }
      bottomSlot={
        <HideFromRelease isReleaseTarget={false}>
          <DEProjectSubHeader.Labels
            labels={['kor', '133B']}
            addLabel={(label: string) => alert(`add ${label}`)}
            deleteLabel={(label: string) => alert(`delete ${label}`)}
          />
        </HideFromRelease>
      }
    />
  );
}

DatasetDetailHeader.displayname = 'DatasetDetailHeader';
